import { drawActorSprite, type ActorSpriteKind } from '../renderer/ActorSprite';
import { drawMobVisual } from '../renderer/mobVisuals/index';
import type { EntityProfileDetail } from '../renderer/tilemap/TileMapRenderer';

interface EntityIdCardOptions {
  isAdmin: boolean;
}

export interface EntityIdCardHandle {
  open(profile: EntityProfileDetail): void;
  close(): void;
  getCurrentProfile(): EntityProfileDetail | null;
  destroy(): void;
}

export function createEntityIdCard(options: EntityIdCardOptions): EntityIdCardHandle {
  const { isAdmin } = options;
  const container = document.createElement('div');
  Object.assign(container.style, {
    position: 'fixed',
    left: '24px',
    top: '24px',
    width: '280px',
    display: 'none',
    zIndex: '6000',
    background: 'rgba(15, 23, 42, 0.96)',
    border: '1px solid #334155',
    borderRadius: '8px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.45)',
    color: '#e2e8f0',
    fontFamily: 'system-ui, sans-serif',
  });

  const header = document.createElement('div');
  Object.assign(header.style, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 10px',
    borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'move',
    userSelect: 'none',
  });
  header.textContent = 'Profile';

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.textContent = 'X';
  Object.assign(closeButton.style, {
    border: 'none',
    background: 'transparent',
    color: '#cbd5e1',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: '700',
    padding: '2px 6px',
    lineHeight: '1',
  });
  header.appendChild(closeButton);

  const body = document.createElement('div');
  Object.assign(body.style, {
    display: 'flex',
    gap: '10px',
    padding: '10px',
  });

  const portrait = document.createElement('canvas');
  portrait.width = 82;
  portrait.height = 82;
  Object.assign(portrait.style, {
    width: '82px',
    height: '82px',
    borderRadius: '6px',
    background: 'rgba(30, 41, 59, 0.7)',
    border: '1px solid rgba(148, 163, 184, 0.3)',
    flex: '0 0 auto',
  });

  const stats = document.createElement('div');
  Object.assign(stats.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: '0',
    fontSize: '0.8rem',
  });

  const nameEl = document.createElement('div');
  Object.assign(nameEl.style, {
    fontWeight: '700',
    fontSize: '0.95rem',
  });
  const typeEl = document.createElement('div');
  const guildEl = document.createElement('div');
  const hpEl = document.createElement('div');
  const mobInventoryValueEl = document.createElement('div');
  const mobInventoryItemsEl = document.createElement('div');
  Object.assign(mobInventoryItemsEl.style, {
    whiteSpace: 'pre-wrap',
    fontSize: '0.75rem',
    opacity: '0.9',
  });
  const adminDividerEl = document.createElement('div');
  Object.assign(adminDividerEl.style, {
    marginTop: '4px',
    paddingTop: '6px',
    borderTop: '1px solid rgba(148, 163, 184, 0.18)',
    fontSize: '0.72rem',
    fontWeight: '700',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#94a3b8',
  });
  adminDividerEl.textContent = 'Admin';
  const idEl = document.createElement('div');
  idEl.style.wordBreak = 'break-all';
  idEl.style.opacity = '0.8';

  stats.append(nameEl, typeEl, guildEl, hpEl, mobInventoryValueEl, mobInventoryItemsEl, adminDividerEl, idEl);
  body.append(portrait, stats);
  container.append(header, body);
  document.body.appendChild(container);
  let currentProfile: EntityProfileDetail | null = null;

  const colorForKind = (kind: ActorSpriteKind): string => {
    if (kind === 'mob') return '#ef4444';
    if (kind === 'self') return '#3b82f6';
    return '#f59e0b';
  };

  const drawPortrait = (profile: EntityProfileDetail): void => {
    const ctx = portrait.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, portrait.width, portrait.height);
    if (profile.kind === 'mob' && profile.entityType) {
      drawMobVisual(ctx, {
        entityType: profile.entityType,
        centerX: portrait.width / 2,
        centerY: portrait.height / 2,
        size: 56,
        rotation: profile.rotation,
        color: colorForKind(profile.kind),
        alpha: 1,
      });
      return;
    }
    drawActorSprite(ctx, {
      centerX: portrait.width / 2,
      centerY: portrait.height / 2,
      size: 56,
      rotation: profile.rotation,
      color: colorForKind(profile.kind),
      alpha: 1,
    });
  };

  const close = (): void => {
    container.style.display = 'none';
    currentProfile = null;
  };

  let dragging = false;
  let dragPointerId: number | null = null;
  let dragOffsetX = 0;
  let dragOffsetY = 0;

  const onDragStart = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    if (event.target === closeButton) return;
    if (container.style.display === 'none') return;
    const rect = container.getBoundingClientRect();
    dragging = true;
    dragPointerId = event.pointerId;
    dragOffsetX = event.clientX - rect.left;
    dragOffsetY = event.clientY - rect.top;
    event.preventDefault();
  };

  const onDragMove = (event: PointerEvent): void => {
    if (!dragging || event.pointerId !== dragPointerId) return;
    const maxLeft = Math.max(0, window.innerWidth - container.offsetWidth);
    const maxTop = Math.max(0, window.innerHeight - container.offsetHeight);
    const nextLeft = Math.max(0, Math.min(event.clientX - dragOffsetX, maxLeft));
    const nextTop = Math.max(0, Math.min(event.clientY - dragOffsetY, maxTop));
    container.style.left = `${nextLeft}px`;
    container.style.top = `${nextTop}px`;
  };

  const onDragEnd = (event: PointerEvent): void => {
    if (event.pointerId !== dragPointerId) return;
    dragging = false;
    dragPointerId = null;
  };

  const open = (profile: EntityProfileDetail): void => {
    currentProfile = { ...profile };
    nameEl.textContent = profile.name;
    const isMob = profile.kind === 'mob';
    typeEl.style.display = isMob ? 'none' : 'block';
    typeEl.textContent = isMob ? '' : 'Adventurer';
    const guildName = profile.guildName?.trim() ?? '';
    const showGuild = !isMob && guildName.length > 0;
    guildEl.style.display = showGuild ? 'block' : 'none';
    guildEl.textContent = showGuild ? `Guild: ${guildName}` : '';
    const hpText =
      typeof profile.hp === 'number'
        ? `HP: ${Math.round(profile.hp)}${typeof profile.maxHp === 'number' ? ` / ${Math.round(profile.maxHp)}` : ''}`
        : 'HP: Unknown';
    hpEl.textContent = hpText;
    if (isMob) {
      const inventoryValue = typeof profile.mobInventoryValue === 'number' ? profile.mobInventoryValue : 0;
      mobInventoryValueEl.style.display = 'block';
      mobInventoryValueEl.textContent = `Inventory Value: ${inventoryValue}`;
      mobInventoryItemsEl.style.display = 'block';
      mobInventoryItemsEl.textContent = formatMobInventoryItems(profile.mobInventoryItemsJson);
    } else {
      mobInventoryValueEl.style.display = 'none';
      mobInventoryItemsEl.style.display = 'none';
      mobInventoryItemsEl.textContent = '';
    }
    const normalizedName = profile.name.trim().toLowerCase();
    const normalizedId = profile.actorId.trim().toLowerCase();
    const showId = isAdmin && normalizedId.length > 0 && normalizedId !== normalizedName;
    adminDividerEl.style.display = showId ? 'block' : 'none';
    idEl.style.display = showId ? 'block' : 'none';
    idEl.textContent = showId ? `Entity ID: ${profile.actorId}` : '';
    drawPortrait(profile);
    container.style.display = 'block';
  };

  const onEscape = (event: KeyboardEvent): void => {
    if (container.style.display === 'none') return;
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    close();
  };

  closeButton.addEventListener('click', close);
  header.addEventListener('pointerdown', onDragStart);
  window.addEventListener('pointermove', onDragMove);
  window.addEventListener('pointerup', onDragEnd);
  window.addEventListener('pointercancel', onDragEnd);
  window.addEventListener('keydown', onEscape, true);

  return {
    open,
    close,
    getCurrentProfile: () => currentProfile ? { ...currentProfile } : null,
    destroy: () => {
      closeButton.removeEventListener('click', close);
      header.removeEventListener('pointerdown', onDragStart);
      window.removeEventListener('pointermove', onDragMove);
      window.removeEventListener('pointerup', onDragEnd);
      window.removeEventListener('pointercancel', onDragEnd);
      window.removeEventListener('keydown', onEscape, true);
      container.remove();
    },
  };
}

function formatMobInventoryItems(rawJson: string | null | undefined): string {
  if (!rawJson || rawJson.trim().length === 0) {
    return 'Inventory: empty';
  }
  try {
    const parsed = JSON.parse(rawJson) as Record<string, unknown>;
    const entries = Object.entries(parsed)
      .filter(([, quantity]) => typeof quantity === 'number' && quantity > 0)
      .sort((a, b) => a[0].localeCompare(b[0]));
    if (entries.length === 0) {
      return 'Inventory: empty';
    }
    return `Inventory: ${entries.map(([itemId, quantity]) => `${itemId} x${Math.floor(quantity as number)}`).join(', ')}`;
  } catch {
    return 'Inventory: unavailable';
  }
}
